import { useState } from 'react';
import { Plus, X, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useReviewTags, useAddReviewTag, useRemoveReviewTag, usePopularTags } from '@/hooks/useReviewTags';
import { useAuth } from '@/hooks/useAuth';

interface ReviewTagsSectionProps {
  reviewId: string;
  reviewUserId: string;
}

const ReviewTagsSection = ({ reviewId, reviewUserId }: ReviewTagsSectionProps) => {
  const { user } = useAuth();
  const { data: tags = [] } = useReviewTags(reviewId);
  const { data: popularTags = [] } = usePopularTags(10);
  const addTag = useAddReviewTag();
  const removeTag = useRemoveReviewTag();

  const [newTag, setNewTag] = useState('');
  const [showInput, setShowInput] = useState(false);

  const isOwner = user?.id === reviewUserId;

  const handleAddTag = async (tagText: string) => {
    if (!tagText.trim()) return;
    
    await addTag.mutateAsync({
      reviewId,
      tag: tagText.trim(),
    });
    
    setNewTag('');
    setShowInput(false);
  };

  const handleRemoveTag = async (tagId: string) => {
    await removeTag.mutateAsync({ tagId, reviewId });
  };

  const suggestedTags = popularTags
    .filter(pt => !tags.find(t => t.tag === pt.tag))
    .slice(0, 5);

  return (
    <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-2 flex-wrap">
        <Tag className="h-4 w-4 text-muted-foreground" />

        {tags.length === 0 && !isOwner && (
          <span className="text-sm text-muted-foreground">No tags yet</span>
        )}

        {tags.map((tag) => (
          <Badge key={tag.id} variant="secondary" className="gap-1">
            {tag.tag}
            {isOwner && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveTag(tag.id);
                }}
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}

        {isOwner && !showInput && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-muted-foreground"
            onClick={(e) => {
              e.stopPropagation();
              setShowInput(true);
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Add tag</span>
          </Button>
        )}
      </div>

      {showInput && isOwner && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              placeholder="Enter tag..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag(newTag);
                } else if (e.key === 'Escape') {
                  setShowInput(false);
                  setNewTag('');
                }
              }}
              maxLength={30}
              className="h-8"
              autoFocus
            />
            <Button size="sm" onClick={() => handleAddTag(newTag)} disabled={addTag.isPending}>
              Add
            </Button>
            <Button size="sm" variant="ghost" onClick={() => {
              setShowInput(false);
              setNewTag('');
            }}>
              Cancel
            </Button>
          </div>

          {suggestedTags.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Suggested tags:</p>
              <div className="flex gap-1 flex-wrap">
                {suggestedTags.map((tag) => (
                  <Badge
                    key={tag.tag}
                    variant="outline"
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleAddTag(tag.tag)}
                  >
                    {tag.tag} ({tag.count})
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewTagsSection;